import React from "react";
import axios from "@/common/axios";
import useOauthToken from "../../hooks/auth/useOauthToken";
import type * as FetchingData from "../../util/fetchingData";
import * as Parsers from "../../util/parsers";
import Result from "../../util/result";

/**
 * The state required to display the conditional parts of the AppBar.
 */
export type UiNavigationData = {
  userDetails: {
    email: string;
    orcidId: null | string;
    orcidAvailable: boolean;
    fullName: string;
    username: string;
    profileImgSrc: null | string;
  };
  visibleTabs: {
    published: boolean;
    inventory: boolean;
    system: boolean;
    myLabGroups: boolean;
  };
  extraHelpLinks: {
    label: string;
    url: string;
  }[];
  bannerImgSrc: string;
  operatedAs: boolean;
  nextMaintenance: null | { startDate: Date };
};

interface UserRenameEvent extends CustomEvent {
  detail: { fullName: string };
}

interface UserSetOrcidEvent extends CustomEvent {
  detail: { orcidId: string | null };
}

interface UserSetEmailEvent extends CustomEvent {
  detail: { email: string };
}

/**
 * This hook fetches the state required to display the conditional parts of
 * the AppBar.
 */
export default function useUiNavigationData(): FetchingData.Fetched<UiNavigationData> {
  const { getToken } = useOauthToken();
  const [loading, setLoading] = React.useState(true);
  const [uiData, setUiData] = React.useState<null | UiNavigationData>(null);
  const [errorMessage, setErrorMessage] = React.useState<null | string>(null);

  React.useEffect(() => {
    const handleUserRename = (event: Event): void => {
      if (!uiData) return;
      const customEvent = event as UserRenameEvent;
      setUiData({
        ...uiData,
        userDetails: {
          ...uiData.userDetails,
          fullName: customEvent.detail.fullName,
        },
      });
    };
    window.addEventListener("USER_RENAME", handleUserRename);
    return () => {
      window.removeEventListener("USER_RENAME", handleUserRename);
    };
  }, [uiData]);

  React.useEffect(() => {
    const handleUserSetOrcid = (event: Event): void => {
      if (!uiData) return;
      const customEvent = event as UserSetOrcidEvent;
      setUiData({
        ...uiData,
        userDetails: {
          ...uiData.userDetails,
          orcidId: customEvent.detail.orcidId,
        },
      });
    };
    window.addEventListener("USER_SET_ORCID", handleUserSetOrcid);
    return () => {
      window.removeEventListener("USER_SET_ORCID", handleUserSetOrcid);
    };
  }, [uiData]);

  React.useEffect(() => {
    const handleUserSetEmail = (event: Event): void => {
      if (!uiData) return;
      const customEvent = event as UserSetEmailEvent;
      setUiData({
        ...uiData,
        userDetails: {
          ...uiData.userDetails,
          email: customEvent.detail.email,
        },
      });
    };
    window.addEventListener("USER_SET_EMAIL", handleUserSetEmail);
    return () => {
      window.removeEventListener("USER_SET_EMAIL", handleUserSetEmail);
    };
  }, [uiData]);

  async function getUiNavigationData(): Promise<void> {
    setUiData(null);
    setLoading(true);
    setErrorMessage(null);
    try {
      const token = await getToken();
      const { data } = await axios.get<unknown>("/api/v1/userDetails/uiNavigationData", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      Parsers.isObject(data)
        .flatMap(Parsers.isNotNull)
        .flatMap((obj) => {
          try {
            const email = Parsers.objectPath(["userDetails", "email"], obj).flatMap(Parsers.isString).elseThrow();
            const orcidId = Parsers.objectPath(["userDetails", "orcidId"], obj)
              .flatMap((o) => Parsers.isString(o).orElseTry(() => Parsers.isNull(o)))
              .elseThrow();
            const orcidAvailable = Parsers.objectPath(["userDetails", "orcidAvailable"], obj)
              .flatMap(Parsers.isBoolean)
              .elseThrow();
            const fullName = Parsers.objectPath(["userDetails", "fullName"], obj).flatMap(Parsers.isString).elseThrow();
            const username = Parsers.objectPath(["userDetails", "username"], obj).flatMap(Parsers.isString).elseThrow();
            const profileImgSrc = Parsers.objectPath(["userDetails", "profileImgSrc"], obj)
              .flatMap((o) => Parsers.isString(o).orElseTry(() => Parsers.isNull(o)))
              .elseThrow();
            const published = Parsers.objectPath(["visibleTabs", "published"], obj)
              .flatMap(Parsers.isBoolean)
              .elseThrow();
            const inventory = Parsers.objectPath(["visibleTabs", "inventory"], obj)
              .flatMap(Parsers.isBoolean)
              .elseThrow();
            const system = Parsers.objectPath(["visibleTabs", "system"], obj).flatMap(Parsers.isBoolean).elseThrow();
            const myLabGroups = Parsers.objectPath(["visibleTabs", "myLabGroups"], obj)
              .flatMap(Parsers.isBoolean)
              .elseThrow();
            const bannerImgSrc = Parsers.objectPath(["bannerImgSrc"], obj).flatMap(Parsers.isString).elseThrow();
            const extraHelpLinks: UiNavigationData["extraHelpLinks"] = Parsers.objectPath(["extraHelpLinks"], obj)
              .flatMap(Parsers.isArray)
              .orElse([]) as UiNavigationData["extraHelpLinks"];
            const operatedAs = Parsers.objectPath(["operatedAs"], obj).flatMap(Parsers.isBoolean).elseThrow();
            const nextMaintenance = Parsers.objectPath(["nextMaintenance"], obj)
              .flatMap((o) =>
                Parsers.isObject(o)
                  .flatMap(Parsers.isNotNull)
                  .flatMap((nextMaintenanceObj) =>
                    Parsers.objectPath(["startDate"], nextMaintenanceObj)
                      .flatMap(Parsers.isString)
                      .flatMap(Parsers.parseDate)
                      .map((startDate) => ({ startDate })),
                  )
                  .orElseTry(() => Parsers.isNull(o)),
              )
              .elseThrow();
            return Result.Ok({
              userDetails: {
                email,
                orcidId,
                orcidAvailable,
                fullName,
                username,
                profileImgSrc,
              },
              visibleTabs: {
                published,
                inventory,
                system,
                myLabGroups,
              },
              bannerImgSrc,
              extraHelpLinks,
              operatedAs,
              nextMaintenance,
            });
          } catch (e) {
            return Result.Error<UiNavigationData>([
              new Error("Could not parse response from /uiNavigationData", {
                cause: e,
              }),
            ]);
          }
        })
        .mapError(([e]) => {
          throw e;
        })
        .do((newUiData) => {
          setUiData(newUiData);
        });
    } catch (e) {
      console.error(e);
      if (e instanceof Error) setErrorMessage(e.message);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    void getUiNavigationData();
  }, []);

  if (loading) return { tag: "loading" };
  if (errorMessage) return { tag: "error", error: errorMessage };
  if (!uiData)
    return {
      tag: "error",
      error: "useUiNavigationData is in an invalid state",
    };
  return { tag: "success", value: uiData };
}
